import React, { useState, useContext, useRef, useEffect } from 'react';
import {
  AppBar,
  Toolbar,
  Typography,
  IconButton,
  Avatar,
  Box,
  TextField,
  InputAdornment,
  Menu,
  MenuItem,
  ListItemIcon,
  ListItemText,
  Divider,
  Badge,
} from '@mui/material';
import {
  Settings,
  Logout,
  Person,
  Timeline,
  HelpOutline,
  PrivacyTip,
  Flag,
  PhoneAndroid,
} from '@mui/icons-material';
import DarkModeIcon from '@mui/icons-material/DarkMode';
import LightModeIcon from '@mui/icons-material/LightMode';
import { CONTEXTS, MODULES } from 'figshelf-modules';
import { useNavigate } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import Icon from './Icon';
import NotificationsMenu from './NotificationsMenu';
import SearchDropdown from './SearchDropdown';
import FloatingMenu from './FloatingMenu';
import { Add } from '@mui/icons-material';
import { useAuthModal } from '../contexts/AuthModalContext';
import { useLogoutBlocker } from '../contexts/LogoutBlockerContext';
import { clearClientCache } from '../utils/clearClientCache';

const { useNotifications } = CONTEXTS;

const TopNav = () => {
  const auth = useContext(CONTEXTS.AuthContext);
  const queryClient = useQueryClient();
  const { unreadCount } = useNotifications();
  const { isDarkMode, toggleTheme } = CONTEXTS.useTheme();
  const navigate = useNavigate();
  const { openLogin, openSignup } = useAuthModal();
  const { runLogout } = useLogoutBlocker();
  const mobileApkUrl = 'https://beta.figshelf.com/releases/Figshelf-Beta.apk';
  const isGuest = !auth?.isSignedIn;
  const [searchQuery, setSearchQuery] = useState('');
  const [debouncedQuery, setDebouncedQuery] = useState('');
  const [showSearchDropdown, setShowSearchDropdown] = useState(false);
  const [anchorEl, setAnchorEl] = useState(null);
  const [notificationAnchorEl, setNotificationAnchorEl] = useState(null);
  const [floatingMenuAnchorEl, setFloatingMenuAnchorEl] = useState(null);
  const searchBoxRef = useRef(null);
  const open = Boolean(anchorEl);
  const notificationOpen = Boolean(notificationAnchorEl);
  const floatingMenuOpen = Boolean(floatingMenuAnchorEl);

  const handleProfileMenuOpen = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleProfileMenuClose = () => {
    setAnchorEl(null);
  };

  const handleNotificationMenuOpen = (event) => {
    setNotificationAnchorEl(event.currentTarget);
  };

  const handleNotificationMenuClose = () => {
    setNotificationAnchorEl(null);
  };

  const handleFloatingMenuOpen = (event) => {
    setFloatingMenuAnchorEl(event.currentTarget);
  };

  const handleFloatingMenuClose = () => {
    setFloatingMenuAnchorEl(null);
  };

  const handleFloatingMenuOptionPress = (option) => {
    switch (option.id) {
      case 'post':
        // Navigate to home with state to open post creator
        navigate('/', { state: { openPostCreator: true } });
        break;
      case 'figure':
        navigate('/create-figure');
        break;
      case 'company':
        // Check if we're already on home page
        if (window.location.pathname === '/') {
          navigate('/', { state: { openCompanyModal: true } });
        } else {
          navigate('/create-company');
        }
        break;
      case 'group':
        // Check if we're already on home page
        if (window.location.pathname === '/') {
          navigate('/', { state: { openGroupModal: true } });
        } else {
          navigate('/create-group');
        }
        break;
      default:
        break;
    }
  };

  // Debounce search query
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedQuery(searchQuery);
    }, 300);

    return () => clearTimeout(timer);
  }, [searchQuery]);

  const handleLogout = async () => {
    handleProfileMenuClose();
    navigate('/', { replace: true });
    await runLogout(async () => {
      await clearClientCache({ queryClient });
      await auth.signOut();
    });
  };

  const handleGoProfile = () => {
    handleProfileMenuClose();
    const username = auth?.user?.username || auth?.userModel?.username || 'me';
    navigate(`/user/${username}`);
  };

  const handleLogin = () => {
    handleProfileMenuClose();
    openLogin();
  };

  const handleRegister = () => {
    handleProfileMenuClose();
    openSignup();
  };

  const handleSettings = () => {
    handleProfileMenuClose();
    navigate('/settings');
  };

  const handleActivity = () => {
    handleProfileMenuClose();
    navigate('/activity');
  };

  const handleHelpSupport = () => {
    handleProfileMenuClose();
    navigate('/help-support');
  };

  const handlePrivacyPolicy = () => {
    handleProfileMenuClose();
    navigate('/privacy-policy');
  };

  const handleReport = () => {
    handleProfileMenuClose();
    navigate('/report');
  };

  const handleGetMobileVersion = () => {
    handleProfileMenuClose();
    window.open(mobileApkUrl, '_blank', 'noopener,noreferrer');
  };

  const handleSearch = (event) => {
    if (event.key === 'Enter' && searchQuery.trim()) {
      // Navigate to search results page
      setShowSearchDropdown(false);
      navigate(`/search?q=${encodeURIComponent(searchQuery.trim())}`);
    }
  };

  const handleSearchChange = (event) => {
    setSearchQuery(event.target.value);
    setShowSearchDropdown(true);
  };

  const handleSearchFocus = () => {
    setShowSearchDropdown(true);
  };

  const handleSearchDropdownClose = () => {
    setShowSearchDropdown(false);
  };

  return (
    <AppBar 
      position="fixed" 
      color="default"
      sx={{ 
        bgcolor: (theme) => theme.palette.background.paper,
        color: (theme) => theme.palette.text.primary,
        borderBottom: (theme) => `1px solid ${theme.palette.divider}`,
        boxShadow: 'none',
        zIndex: 1200,
      }}
    >
      <Toolbar sx={{ justifyContent: 'center', position: 'relative' }}>
        
        <Box sx={{ display: 'flex', alignItems: 'center', position: 'absolute', left: 0, ml: 2 }}>
          {/* Logo */}
          <Box
            component="img"
            src={MODULES.Resources.logos.logo_1}
            alt="Figshelf"
            sx={{
              height: 36,
              objectFit: 'contain',
              display: 'block'
            }}
            onClick={() => navigate('/')}
            style={{ cursor: 'pointer' }}
          />
        </Box>

        {/* Center Section - Search */}
        <Box sx={{ flex: 1, maxWidth: 500, position: 'relative' }} ref={searchBoxRef}>
          <TextField
            fullWidth
            variant="outlined"
            placeholder="Search figures, users, posts..."
            value={searchQuery}
            onChange={handleSearchChange}
            onKeyPress={handleSearch}
            onFocus={handleSearchFocus}
            size="small"
            sx={{
              '& .MuiOutlinedInput-root': {
                backgroundColor: (theme) => theme.palette.action.hover,
                borderRadius: 3,
                '& fieldset': {
                  borderColor: (theme) => theme.palette.divider,
                },
                '&:hover fieldset': {
                  borderColor: (theme) => theme.palette.divider,
                },
              },
            }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <Icon name="search" size={18} />
                </InputAdornment>
              ),
            }}
          />
          {showSearchDropdown && (
            <SearchDropdown
              searchQuery={debouncedQuery}
              onClose={handleSearchDropdownClose}
              anchorEl={searchBoxRef.current}
            />
          )}
        </Box>

        {/* Right Section - Notifications and Profile */}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, position: 'absolute', right: 16 }}>
      
              {/* Plus Button */}
          <IconButton
            onClick={handleFloatingMenuOpen}
            size="small"
            sx={{
              color: 'primary.main',
              width: 40,
              height: 40,
              '&:hover': {
                bgcolor: 'primary.dark',
                boxShadow: '0 2px 8px rgba(73, 160, 173, 0.3)', 
                color: 'white',
              },
              transition: 'all 0.2s ease-in-out',
            }}
            aria-controls={floatingMenuOpen ? 'floating-menu' : undefined}
            aria-haspopup="true"
            aria-expanded={floatingMenuOpen ? 'true' : undefined}
          >
            <Add />
          </IconButton>

          {/* Theme Toggle */}
          <IconButton onClick={toggleTheme} size="small">
            {isDarkMode ? <LightModeIcon /> : <DarkModeIcon />}
          </IconButton>
          
          {/* Notifications */}
          <IconButton 
            color="inherit" 
            sx={{ position: 'relative' }}
            onClick={handleNotificationMenuOpen}
            aria-controls={notificationOpen ? 'notification-menu' : undefined}
            aria-haspopup="true"
            aria-expanded={notificationOpen ? 'true' : undefined}
          >
            <Badge badgeContent={unreadCount} color="error">
              <Icon name="notification" />
            </Badge>
          </IconButton>


          {/* Profile Menu */}
          <IconButton
            onClick={handleProfileMenuOpen}
            size="small"
            sx={{ ml: 2 }}
            aria-controls={open ? 'account-menu' : undefined}
            aria-haspopup="true"
            aria-expanded={open ? 'true' : undefined}
          >
            <Avatar
              sx={{ width: 32, height: 32, bgcolor: 'primary.main' }}
              src={auth?.user?.profile?.currentAvatar?.url}
            >
            </Avatar>
          </IconButton>
        </Box>
      </Toolbar>

      {/* Profile Menu Dropdown */}
      <Menu
        anchorEl={anchorEl}
        id="account-menu"
        open={open}
        onClose={handleProfileMenuClose}
        PaperProps={{
          elevation: 3,
          sx: {
            width: 280,
            overflow: 'visible',
            filter: 'drop-shadow(0px 2px 8px rgba(0,0,0,0.15))',
            mt: 1.5,
            borderRadius: 2,
            '&:before': {
              content: '""',
              display: 'block',
              position: 'absolute',
              top: 0,
              right: 14,
              width: 10,
              height: 10,
              bgcolor: 'background.paper',
              transform: 'translateY(-50%) rotate(45deg)',
              zIndex: 0,
            },
          },
        }}
        transformOrigin={{ horizontal: 'right', vertical: 'top' }}
        anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
      >
        {/* User Profile Header */}
        <Box 
          sx={{ 
            px: 2, 
            py: 2, 
            cursor: 'pointer',
            '&:hover': {
              bgcolor: 'action.hover',
            },
          }}
          onClick={isGuest ? handleLogin : handleGoProfile}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
            <Avatar
              sx={{ width: 48, height: 48, bgcolor: 'primary.main' }}
              src={auth.user?.profile?.currentAvatar?.url}
            />
            <Box sx={{ flex: 1, minWidth: 0 }}>
              <Typography 
                variant="subtitle1" 
                sx={{ 
                  fontWeight: 600,
                  lineHeight: 1.2,
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap',
                }}
              >
                {isGuest ? 'Guest' : (auth.user?.profile?.displayName || auth.user?.username || 'User')}
              </Typography>
              <Typography 
                variant="body2" 
                color="text.secondary"
                sx={{ 
                  lineHeight: 1.2,
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap',
                }}
              >
                {isGuest ? 'Sign up to personalize your feed' : `@${auth.user?.username || 'username'}`}
              </Typography>
            </Box>
          </Box>
        </Box>
        
        <Divider />
        
        {/* Menu Items */}
        {!isGuest && (
          <MenuItem onClick={handleSettings} sx={{ py: 1.5 }}>
            <ListItemIcon>
              <Settings fontSize="small" />
            </ListItemIcon>
            <ListItemText 
              primary="Settings" 
              primaryTypographyProps={{ fontWeight: 500 }}
            />
          </MenuItem>
        )}
        
        {!isGuest && (
          <MenuItem onClick={handleActivity} sx={{ py: 1.5 }}>
            <ListItemIcon>
              <Timeline fontSize="small" />
            </ListItemIcon>
            <ListItemText 
              primary="Activity" 
              primaryTypographyProps={{ fontWeight: 500 }}
            />
          </MenuItem>
        )}
        
        <Divider />
        
        <MenuItem onClick={handleHelpSupport} sx={{ py: 1.5 }}>
          <ListItemIcon>
            <HelpOutline fontSize="small" />
          </ListItemIcon>
          <ListItemText 
            primary="Help & Support" 
            primaryTypographyProps={{ fontWeight: 500 }}
          />
        </MenuItem>
        
        <MenuItem onClick={handlePrivacyPolicy} sx={{ py: 1.5 }}>
          <ListItemIcon>
            <PrivacyTip fontSize="small" />
          </ListItemIcon>
          <ListItemText 
            primary="Privacy Policy" 
            primaryTypographyProps={{ fontWeight: 500 }}
          />
        </MenuItem>

        <MenuItem onClick={handleGetMobileVersion} sx={{ py: 1.5 }}>
          <ListItemIcon>
            <PhoneAndroid fontSize="small" />
          </ListItemIcon>
          <ListItemText
            primary="Get Mobile Version"
            primaryTypographyProps={{ fontWeight: 500 }}
          />
        </MenuItem>
        
        <MenuItem onClick={handleReport} sx={{ py: 1.5 }}>
          <ListItemIcon>
            <Flag fontSize="small" />
          </ListItemIcon>
          <ListItemText 
            primary="Report" 
            primaryTypographyProps={{ fontWeight: 500 }}
          />
        </MenuItem>
        
        <Divider />

        {isGuest && (
          <>
            <MenuItem onClick={handleLogin} sx={{ py: 1.5 }}>
              <ListItemIcon>
                <Person fontSize="small" />
              </ListItemIcon>
              <ListItemText 
                primary="Log in" 
                primaryTypographyProps={{ fontWeight: 500 }}
              />
            </MenuItem>
            <MenuItem onClick={handleRegister} sx={{ py: 1.5 }}>
              <ListItemIcon>
                <Person fontSize="small" />
              </ListItemIcon>
              <ListItemText 
                primary="Sign up" 
                primaryTypographyProps={{ fontWeight: 500 }}
              />
            </MenuItem>
            <Divider />
          </>
        )}
        
        {!isGuest && (
          <MenuItem onClick={handleLogout} sx={{ py: 1.5, color: 'error.main' }}>
            <ListItemIcon>
              <Logout fontSize="small" color="error" />
            </ListItemIcon>
            <ListItemText 
              primary="Logout" 
              primaryTypographyProps={{ fontWeight: 500 }}
            />
          </MenuItem>
        )}
      </Menu>

      {/* Notifications Menu */}
      <NotificationsMenu
        anchorEl={notificationAnchorEl}
        open={notificationOpen}
        onClose={handleNotificationMenuClose}
      />

      {/* Floating Menu */}
      <FloatingMenu
        anchorEl={floatingMenuAnchorEl}
        open={floatingMenuOpen}
        onClose={handleFloatingMenuClose}
        onOptionPress={handleFloatingMenuOptionPress}
      />
    </AppBar>
  );
};

export default TopNav;
